import {OtsDateTime} from "../../components/OtsDateTime";
import {
  ArrowPathIcon,
  ArrowRightStartOnRectangleIcon,
  BellAlertIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  KeyIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  UserIcon
} from "@heroicons/react/20/solid";
import {PersonEntity} from "@ots-frontend/ots-data";
import {ReactNode, useState} from "react";
import {ChangePasswordDialog, ChangePasswordRequest} from "../../account/ChangePasswordDialog";
import {PasswordPolicy} from "../../account/password-policy";
import {AdminPage} from "../../components/AdminPage";
import {AdminPanel} from "../../components/AdminPanel";
import {DescriptionItem, DescriptionList} from "../../components/DescriptionList";
import {OtsAvatar} from "../../components/OtsAvatar";
import {OtsActionButton, OtsActionLink} from "../../components/OtsButton";
import {OtsInlineAlert} from "../../components/OtsInlineAlert";
import {StatusPill, StatusTone} from "../../components/StatusPill";
import {OtsBrand, OtsColorSchemePreference, OtsThemeRoot} from "../../theme/OtsTheme";
import {
  formatProfileDate,
  getPreferredLanguageLabel,
  getRegionsLabel,
  getTeamMembershipStatus,
  sortCapabilitiesForProfile,
  sortRolesByName,
  sortTeamsForProfile,
  TeamMembershipStatus,
  UserProfileCapability,
  UserProfileRole,
  UserProfileTeam
} from "./user-profile-utils";

export type UserProfileProps = {
  person?: PersonEntity;
  roles?: UserProfileRole[];
  /**
   * What the person is set up to do -- Driver, Dispatcher. Same contract as teams: leave undefined,
   * with no loading or error either, to omit the section for a host that cannot load them.
   */
  capabilities?: UserProfileCapability[];
  capabilitiesLoading?: boolean;
  /** Why capabilities could not be loaded. Shown inside the section; the rest of the profile still works. */
  capabilitiesError?: ReactNode;
  /** Names for the person's regions. Without them the profile shows a count. */
  regionNames?: string[];
  /**
   * Teams the person belongs to. Leave undefined -- with no loading or error either -- to omit the
   * section, e.g. for a host that cannot load teams.
   */
  teams?: UserProfileTeam[];
  teamsLoading?: boolean;
  /** Why teams could not be loaded. Shown inside the section; the rest of the profile still works. */
  teamsError?: ReactNode;
  brand?: OtsBrand;
  /**
   * Light or dark. Defaults to "system": the host page's theme (WaterGo's GWT shell sets one), else
   * the operating system's.
   */
  colorScheme?: OtsColorSchemePreference;
  loading?: boolean;
  /** Why the profile could not be loaded. */
  error?: ReactNode;
  onRetry?: () => void;
  /**
   * Sends a password change. Omit where this host cannot change passwords -- the control is hidden
   * rather than shown and inert. See `ChangePasswordDialog` for how to report failures.
   */
  onChangePassword?: (request: ChangePasswordRequest) => Promise<void> | void;
  /**
   * Who owns this person's password when it is not OTS, e.g. "your organization's single sign-on".
   * Read into a sentence, so phrase it that way. Replaces the change control.
   */
  passwordManagedBy?: string;
  passwordPolicy?: PasswordPolicy;
  /** Extra header actions, placed before Sign out and Change password. */
  actions?: ReactNode;
  onSignOut?: () => void;
  /** Link or host callback for the notification-subscription manager. */
  notificationSettingsHref?: string;
  onManageNotifications?: () => void;
  /** Opens the change-password dialog on first render, for deep links. */
  defaultChangePasswordOpen?: boolean;
  className?: string;
};

/**
 * The signed-in person's own profile: who they are, what they can access, and their password.
 *
 * Presentational -- the host loads the person and roles and performs the password change -- so the
 * same view serves HubGo, DispatchGo and the dashboard mounted in WaterGo.
 */
export const UserProfile = ({
  person,
  roles = [],
  capabilities,
  capabilitiesLoading = false,
  capabilitiesError,
  regionNames,
  teams,
  teamsLoading = false,
  teamsError,
  brand,
  colorScheme,
  loading = false,
  error,
  onRetry,
  onChangePassword,
  passwordManagedBy,
  passwordPolicy,
  actions,
  onSignOut,
  notificationSettingsHref,
  onManageNotifications,
  defaultChangePasswordOpen = false,
  className
}: UserProfileProps) => {
  const [changePasswordOpen, setChangePasswordOpen] = useState(defaultChangePasswordOpen);

  const canChangePassword = Boolean(person && onChangePassword && !passwordManagedBy && !loading && !error);

  const headerActions = canChangePassword || onSignOut || actions ? (
    <>
      {actions}
      {onSignOut && (
        <OtsActionButton variant="secondary" icon={<ArrowRightStartOnRectangleIcon/>} onClick={onSignOut}>
          Sign out
        </OtsActionButton>
      )}
      {canChangePassword && (
        <OtsActionButton variant="primary" icon={<KeyIcon/>} onClick={() => setChangePasswordOpen(true)}>
          Change password
        </OtsActionButton>
      )}
    </>
  ) : undefined;

  return (
    <OtsThemeRoot
      brand={brand}
      colorScheme={colorScheme}
      defaultColorScheme="system"
      data-testid="user-profile-tw"
      // Paints to the bottom of whatever hosts it. GWT mounts this into a full-height scrolling panel
      // through a display:contents root, and AdminPage's own min-h-full cannot resolve against a
      // parent with no height -- without this, a short profile in dark mode ends in a white band.
      className="min-h-full bg-[var(--ots-background)]">
      <AdminPage
        className={className}
        eyebrow="Account"
        title="My profile"
        subtitle="How you appear across OTS applications. Contact an administrator to change your details or access."
        actions={headerActions}>
        {/* Laid out by its own width, not the viewport's: GWT mounts this in panels far narrower
            than the window, where viewport breakpoints would pick the desktop layout. */}
        <div className="@container">
          {loading ? (
            <UserProfileSkeleton/>
          ) : error ? (
            <OtsInlineAlert
              tone="danger"
              title="Couldn't load your profile"
              className="mt-8"
              action={onRetry && (
                <OtsActionButton size="compact" icon={<ArrowPathIcon/>} onClick={onRetry}>
                  Try again
                </OtsActionButton>
              )}>
              {error}
            </OtsInlineAlert>
          ) : !person ? (
            <OtsInlineAlert className="mt-8">There is no profile for this sign-in.</OtsInlineAlert>
          ) : (
            <>
              <ProfileSummary person={person}/>

              <div className="mt-6 grid gap-6 @3xl:grid-cols-3">
                <div className="flex min-w-0 flex-col gap-6 @3xl:col-span-2">
                  <DetailsPanel person={person} regionNames={regionNames}/>
                  {(teams || teamsLoading || teamsError) && (
                    <TeamsPanel teams={teams} loading={teamsLoading} error={teamsError}/>
                  )}
                </div>
                <div className="flex min-w-0 flex-col gap-6">
                  {(notificationSettingsHref || onManageNotifications) && (
                    <NotificationsPanel href={notificationSettingsHref} onOpen={onManageNotifications}/>
                  )}
                  <SecurityPanel
                    person={person}
                    canChangePassword={canChangePassword}
                    passwordManagedBy={passwordManagedBy}/>
                  <RolesPanel roles={roles}/>
                  {(capabilities || capabilitiesLoading || capabilitiesError) && (
                    <CapabilitiesPanel
                      capabilities={capabilities}
                      loading={capabilitiesLoading}
                      error={capabilitiesError}/>
                  )}
                </div>
              </div>

              <RecordFooter person={person}/>
            </>
          )}
        </div>
      </AdminPage>

      {canChangePassword && person && onChangePassword && (
        <ChangePasswordDialog
          open={changePasswordOpen}
          onClose={() => setChangePasswordOpen(false)}
          onSubmit={onChangePassword}
          userName={person.userName}
          policy={passwordPolicy}
          brand={brand}
          // Portaled out of the page, but context still reaches it: match whatever the page resolved.
          colorScheme="inherit"/>
      )}
    </OtsThemeRoot>
  );
};

const ProfileSummary = ({person}: {person: PersonEntity}) => {
  return (
    <AdminPanel className="mt-8" bodyClassName="flex flex-col gap-5 p-5 @md:flex-row @md:items-center @md:gap-6 @md:p-7">
      {/* The name is read out right beside it. */}
      <OtsAvatar name={person.name} size="xl" decorative/>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2.5">
          <h2 className="min-w-0 break-words font-[family-name:var(--ots-font-display)] text-xl font-semibold tracking-[-0.01em] text-[var(--ots-secondary)] sm:text-2xl">
            {person.name}
          </h2>
          {person.active === false && <StatusPill tone="neutral">Inactive</StatusPill>}
        </div>
        <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-[var(--ots-text-muted)]">
          {person.userName && (
            <li className="flex min-w-0 items-center gap-1.5">
              <UserIcon aria-hidden="true" className="size-4 shrink-0"/>
              <span className="sr-only">Username </span>
              <span className="truncate">{person.userName}</span>
            </li>
          )}
          {person.companyName && (
            <li className="flex min-w-0 items-center gap-1.5">
              <BuildingOffice2Icon aria-hidden="true" className="size-4 shrink-0"/>
              <span className="sr-only">Company </span>
              <span className="truncate">{person.companyName}</span>
            </li>
          )}
        </ul>
      </div>
    </AdminPanel>
  );
};

const linkClassName = "text-[var(--ots-primary)] underline-offset-2 hover:underline";

const DetailsPanel = ({person, regionNames, className}: {person: PersonEntity; regionNames?: string[]; className?: string}) => (
  <AdminPanel title="Details" className={className} bodyClassName="px-5 py-6 sm:px-7">
    <DescriptionList className="!grid-cols-1 @md:!grid-cols-2">
      <DescriptionItem label="Full name">{person.name}</DescriptionItem>
      <DescriptionItem label="Username">{person.userName}</DescriptionItem>
      <DescriptionItem label="Email">
        {person.emailAddress && <a className={linkClassName} href={`mailto:${person.emailAddress}`}>{person.emailAddress}</a>}
      </DescriptionItem>
      <DescriptionItem label="Phone">
        {person.phoneNumber && <a className={linkClassName} href={`tel:${person.phoneNumber}`}>{person.phoneNumber}</a>}
      </DescriptionItem>
      <DescriptionItem label="Company">{person.companyName}</DescriptionItem>
      <DescriptionItem label="Preferred language">{getPreferredLanguageLabel(person)}</DescriptionItem>
      <DescriptionItem label="Regions">{getRegionsLabel(person, regionNames)}</DescriptionItem>
    </DescriptionList>
  </AdminPanel>
);

const SecurityPanel = ({
  person,
  canChangePassword,
  passwordManagedBy
}: {
  person: PersonEntity;
  canChangePassword: boolean;
  passwordManagedBy?: string;
}) => {
  let status: ReactNode = null;
  if (passwordManagedBy) {
    status = <StatusPill tone="info">Single sign-on</StatusPill>;
  } else if (person.passwordSet === true) {
    status = <StatusPill tone="success">Set</StatusPill>;
  } else if (person.passwordSet === false) {
    status = <StatusPill tone="warning">Not set</StatusPill>;
  }

  let guidance: string;
  if (passwordManagedBy) {
    guidance = `Your password is managed by ${passwordManagedBy}. Change it there.`;
  } else if (canChangePassword) {
    guidance = "Use Change password above. Pick one you don't use anywhere else.";
  } else {
    guidance = "Ask an administrator to change your password.";
  }

  return (
    <AdminPanel title="Sign-in & security" bodyClassName="px-5 py-5 sm:px-6">
      <div className="flex items-start gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[var(--ots-primary-container)] text-[var(--ots-primary)]">
          <KeyIcon aria-hidden="true" className="size-[18px]"/>
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-[var(--ots-secondary)]">Password</p>
            {status}
          </div>
          <p className="mt-1 text-sm leading-6 text-[var(--ots-text-muted)]">{guidance}</p>
        </div>
      </div>
    </AdminPanel>
  );
};

const NotificationsPanel = ({href, onOpen}: {href?: string; onOpen?: () => void}) => (
  <AdminPanel title="Notifications" bodyClassName="px-5 py-5 sm:px-6">
    <div className="flex items-start gap-3">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[var(--ots-primary-container)] text-[var(--ots-primary)]">
        <BellAlertIcon aria-hidden="true" className="size-[18px]"/>
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-6 text-[var(--ots-text-muted)]">Choose which updates you receive and whether they arrive by email, push, or SMS.</p>
        <div className="mt-3">
          {href ? (
            <OtsActionLink href={href} size="compact" variant="tonal" onClick={onOpen}>Manage notifications</OtsActionLink>
          ) : (
            <OtsActionButton size="compact" variant="tonal" onClick={onOpen}>Manage notifications</OtsActionButton>
          )}
        </div>
      </div>
    </div>
  </AdminPanel>
);

const RolesPanel = ({roles}: {roles: UserProfileRole[]}) => (
  <AdminPanel
    title="Roles"
    description={roles.length ? `${roles.length} assigned` : undefined}
    bodyClassName="px-5 py-5 sm:px-6">
    {roles.length ? (
      <ul aria-label="Roles" className="flex flex-wrap gap-2">
        {sortRolesByName(roles).map((role) => (
          <li
            key={role.id}
            title={role.description}
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--ots-outline-variant)] bg-[var(--ots-background)] px-2.5 py-1 text-xs font-semibold text-[var(--ots-secondary)]">
            <ShieldCheckIcon aria-hidden="true" className="size-3.5 text-[var(--ots-primary)]"/>
            {role.name}
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-sm leading-6 text-[var(--ots-text-muted)]">
        No roles assigned. Ask an administrator if you're missing access.
      </p>
    )}
  </AdminPanel>
);

const CapabilitiesPanel = ({
  capabilities,
  loading,
  error
}: {
  capabilities?: UserProfileCapability[];
  loading: boolean;
  error?: ReactNode;
}) => {
  const sorted = capabilities ? sortCapabilitiesForProfile(capabilities) : [];

  let body: ReactNode;
  if (loading) {
    body = (
      <div role="status" aria-label="Loading capabilities" className="flex animate-pulse gap-2">
        <div className={`h-6 w-20 ${skeletonBlock}`}/>
        <div className={`h-6 w-24 ${skeletonBlock}`}/>
      </div>
    );
  } else if (error) {
    body = <p className="text-sm leading-6 text-[var(--ots-text-muted)]">{error}</p>;
  } else if (!sorted.length) {
    body = (
      <p className="text-sm leading-6 text-[var(--ots-text-muted)]">
        No capabilities assigned. Ask an administrator if you should be set up for driving or dispatch.
      </p>
    );
  } else {
    body = (
      <ul aria-label="Capabilities" className="flex flex-wrap gap-2">
        {sorted.map((capability) => (
          <li
            key={capability.id}
            className={[
              "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold text-[var(--ots-secondary)]",
              // The primary one is the answer to "what does this person do", so it reads first and filled.
              capability.primary
                ? "border-transparent bg-[var(--ots-primary-container)]"
                : "border-[var(--ots-outline-variant)] bg-[var(--ots-background)]"
            ].join(" ")}>
            <BriefcaseIcon aria-hidden="true" className="size-3.5 text-[var(--ots-primary)]"/>
            {capability.name}
            {capability.primary && <span className="font-normal text-[var(--ots-text-muted)]">· Primary</span>}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <AdminPanel
      title="Capabilities"
      description={!loading && !error && sorted.length ? `${sorted.length} assigned` : undefined}
      bodyClassName="px-5 py-5 sm:px-6">
      {body}
    </AdminPanel>
  );
};

const teamStatusPills: Record<TeamMembershipStatus, {tone: StatusTone; label: (team: UserProfileTeam) => string}> = {
  active: {tone: "success", label: () => "Active"},
  upcoming: {tone: "info", label: (team) => `Starts ${formatProfileDate(team.effectiveStartDttm)}`},
  ended: {tone: "neutral", label: (team) => `Ended ${formatProfileDate(team.effectiveEndDttm)}`},
  inactive: {tone: "neutral", label: () => "Inactive"}
};

const panelMessageClassName = "px-5 py-5 text-sm leading-6 text-[var(--ots-text-muted)] sm:px-7";

const TeamsPanel = ({teams, loading, error}: {teams?: UserProfileTeam[]; loading: boolean; error?: ReactNode}) => {
  const now = Date.now();
  const sorted = teams ? sortTeamsForProfile(teams, now) : [];
  const activeCount = sorted.filter((team) => getTeamMembershipStatus(team, now) === "active").length;

  let summary: string | undefined;
  if (!loading && !error && sorted.length) {
    summary = activeCount === sorted.length ? `${activeCount} active` : `${activeCount} active · ${sorted.length} total`;
  }

  let body: ReactNode;
  if (loading) {
    body = (
      <div role="status" aria-label="Loading teams" className="animate-pulse space-y-3 px-5 py-5 sm:px-7">
        <div className={`h-4 w-56 max-w-full ${skeletonBlock}`}/>
        <div className={`h-4 w-40 max-w-full ${skeletonBlock}`}/>
      </div>
    );
  } else if (error) {
    body = <p className={panelMessageClassName}>{error}</p>;
  } else if (!sorted.length) {
    body = <p className={panelMessageClassName}>You're not on any teams yet.</p>;
  } else {
    body = (
      <ul aria-label="Teams" className="divide-y divide-[var(--ots-outline-variant)]">
        {sorted.map((team) => {
          const pill = teamStatusPills[getTeamMembershipStatus(team, now)];
          const context = [team.scopeName, team.parentTeamName && `Part of ${team.parentTeamName}`]
            .filter(Boolean)
            .join(" · ");

          return (
            <li key={team.id} className="flex items-start gap-3 px-5 py-4 sm:px-7">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[var(--ots-primary-container)] text-[var(--ots-primary)]">
                <UserGroupIcon aria-hidden="true" className="size-[18px]"/>
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="min-w-0 break-words text-sm font-semibold text-[var(--ots-secondary)]">{team.name}</p>
                  <StatusPill tone={pill.tone}>{pill.label(team)}</StatusPill>
                </div>
                {context && <p className="mt-0.5 text-xs text-[var(--ots-text-muted)]">{context}</p>}
                {team.description && <p className="mt-1.5 text-sm leading-6 text-[var(--ots-text-muted)]">{team.description}</p>}
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <AdminPanel title="Teams" description={summary}>
      {body}
    </AdminPanel>
  );
};

const RecordFooter = ({person}: {person: PersonEntity}) => {
  if (person.createDttm == null && person.lastModifiedDttm == null) return null;

  return (
    <p className="mt-6 text-xs text-[var(--ots-text-muted)]">
      {person.createDttm != null && <>Profile created <OtsDateTime value={person.createDttm} displayTime={false}/></>}
      {person.createDttm != null && person.lastModifiedDttm != null && " · "}
      {person.lastModifiedDttm != null && <>Last updated <OtsDateTime value={person.lastModifiedDttm} displayTime={false}/></>}
    </p>
  );
};

const skeletonBlock = "rounded bg-[var(--ots-surface-variant)]";

const UserProfileSkeleton = () => (
  <div role="status" aria-label="Loading profile" className="mt-8 animate-pulse space-y-6">
    <div className="flex items-center gap-6 rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] p-7">
      <div className="size-20 shrink-0 rounded-full bg-[var(--ots-surface-variant)]"/>
      <div className="flex-1 space-y-3">
        <div className={`h-6 w-48 max-w-full ${skeletonBlock}`}/>
        <div className={`h-4 w-72 max-w-full ${skeletonBlock}`}/>
      </div>
    </div>
    <div className="grid gap-6 @3xl:grid-cols-3">
      <div className="h-72 rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] @3xl:col-span-2"/>
      <div className="h-72 rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]"/>
    </div>
  </div>
);
